import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, Phone, Mail, Clock, ArrowRight, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { businessInfo } from '../data/mock';
import Faq from '../components/Faq';
import cotizarImage from '../media/Productos.webp';
import { track, openWhatsApp, trackPhone, trackEmail } from '../lib/analytics';
import { toast } from 'sonner';

const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            entry.target.classList.remove('scroll-hidden');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = ref.current?.querySelectorAll('.scroll-hidden');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
};

const Cotizar = () => {
  const sectionRef = useScrollReveal();

  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    telefono: '',
    ciudad: '',
    mensaje: ''
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const camposFaltantes = () =>
    ['nombre', 'telefono', 'mensaje'].filter((f) => !formData[f]);

  const validar = () => {
    const faltan = camposFaltantes();
    if (faltan.length) {
      track('quote_form_rejected', { missing: faltan });
      toast.error('Por favor completa tu nombre, teléfono y describe lo que necesitas');
      return false;
    }
    return true;
  };

  // WhatsApp interpreta *texto* como negrita; en un correo se veria literal.
  const buildMessage = (negritas) => {
    const b = negritas ? '*' : '';
    const lineas = [`${b}SOLICITUD DE COTIZACIÓN${b}`, '', `${b}Nombre:${b} ${formData.nombre}`];
    if (formData.empresa) lineas.push(`${b}Empresa:${b} ${formData.empresa}`);
    lineas.push(`${b}Teléfono:${b} ${formData.telefono}`);
    if (formData.ciudad) lineas.push(`${b}Ciudad:${b} ${formData.ciudad}`);
    lineas.push('', `${b}Detalles del pedido:${b}`, formData.mensaje);
    return lineas.join('\n');
  };

  const propiedadesDelLead = () => ({
    has_empresa: Boolean(formData.empresa),
    has_ciudad: Boolean(formData.ciudad),
    mensaje_length: formData.mensaje.length,
  });

  const limpiar = () =>
    setFormData({ nombre: '', empresa: '', telefono: '', ciudad: '', mensaje: '' });

  const handleWhatsAppSubmit = () => {
    if (!validar()) return;
    track('quote_form_submitted', { ...propiedadesDelLead(), channel: 'whatsapp' });
    openWhatsApp('cotizar_formulario', buildMessage(true));
    toast.success('¡Redirigiendo a WhatsApp!');
    limpiar();
  };

  // Salidas alternas. El sitio es estatico y no hay backend donde guardar el
  // lead: si WhatsApp no abre -escritorio sin WhatsApp Web, popups bloqueados-
  // sin esto la solicitud se perderia por completo.
  const handleEmailSubmit = () => {
    if (!validar()) return;
    track('quote_form_submitted', { ...propiedadesDelLead(), channel: 'email' });
    const asunto = `Solicitud de cotización - ${formData.nombre}`;
    window.location.href =
      `mailto:${businessInfo.email}?subject=${encodeURIComponent(asunto)}` +
      `&body=${encodeURIComponent(buildMessage(false))}`;
    toast.success('Abriendo tu correo…');
  };

  const handleCopy = async () => {
    if (!validar()) return;
    try {
      await navigator.clipboard.writeText(buildMessage(false));
      track('quote_form_copied', propiedadesDelLead());
      toast.success('Mensaje copiado. Pégalo donde prefieras enviarlo.');
    } catch (e) {
      toast.error('No se pudo copiar. Selecciona el texto manualmente.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Cotiza tu pedido | Maderas El Ocote - Torreón</title>
        <meta
          name="description"
          content="Solicita tu cotización de madera de pino para construcción, carpintería e industria en Torreón, Coahuila. Respuesta por WhatsApp o formulario."
        />
        <link rel="canonical" href="https://www.maderaselocote.com/cotizar" />
      </Helmet>
      <div ref={sectionRef} className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={cotizarImage}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-wood-900/95 via-wood-900/80 to-wood-900/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-wood-300 uppercase tracking-widest mb-4 animate-fade-in">Cotización</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white leading-[1.1] tracking-tight mb-6 animate-fade-up">
              Solicita tu <span className="text-wood-300">cotización</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 leading-relaxed max-w-lg animate-fade-up" style={{ animationDelay: '200ms' }}>
              Completa el formulario y te contactaremos por WhatsApp para atender tu solicitud.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-12">
            {/* Main Form */}
            <div className="lg:col-span-2 scroll-hidden">
              <div className="p-8 sm:p-10 rounded-2xl border border-wood-200/60 bg-white">
                <h2 className="text-2xl font-heading font-bold text-wood-900 mb-8">Información de contacto</h2>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="nombre" className="text-wood-900 font-medium">Nombre completo *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => handleChange('nombre', e.target.value)}
                        placeholder="Juan Pérez"
                        className="mt-2 rounded-xl border-wood-200 focus:border-wood-300 focus:ring-wood-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="empresa" className="text-wood-900 font-medium">Empresa (opcional)</Label>
                      <Input
                        id="empresa"
                        value={formData.empresa}
                        onChange={(e) => handleChange('empresa', e.target.value)}
                        placeholder="Mi Empresa SA"
                        className="mt-2 rounded-xl border-wood-200 focus:border-wood-300 focus:ring-wood-300"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="telefono" className="text-wood-900 font-medium">Teléfono / WhatsApp *</Label>
                      <Input
                        id="telefono"
                        value={formData.telefono}
                        onChange={(e) => handleChange('telefono', e.target.value)}
                        placeholder="871 123 4567"
                        className="mt-2 rounded-xl border-wood-200 focus:border-wood-300 focus:ring-wood-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ciudad" className="text-wood-900 font-medium">Ciudad</Label>
                      <Input
                        id="ciudad"
                        value={formData.ciudad}
                        onChange={(e) => handleChange('ciudad', e.target.value)}
                        placeholder="Torreón, Coahuila"
                        className="mt-2 rounded-xl border-wood-200 focus:border-wood-300 focus:ring-wood-300"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="mensaje" className="text-wood-900 font-medium">¿Qué necesitas? *</Label>
                    <Textarea
                      id="mensaje"
                      value={formData.mensaje}
                      onChange={(e) => handleChange('mensaje', e.target.value)}
                      placeholder="Describe el tipo de producto, cantidades aproximadas, medidas si las conoces, y cualquier otra información relevante..."
                      className="mt-2 min-h-40 rounded-xl border-wood-200 focus:border-wood-300 focus:ring-wood-300"
                    />
                    <p className="text-sm text-wood-400 mt-2">
                      Ejemplo: "Necesito 50 puntales de 4x4 pulgadas por 10 pies de largo"
                    </p>
                  </div>

                  <Button
                    onClick={handleWhatsAppSubmit}
                    size="lg"
                    className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-xl py-6 text-base shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Enviar por WhatsApp
                  </Button>

                  <p className="text-sm text-center text-wood-400">
                    Te responderemos lo antes posible con tu cotización personalizada
                  </p>

                  <div className="pt-4 border-t border-wood-200/60">
                    <p className="text-xs text-center text-wood-400 mb-3">
                      ¿No se abrió WhatsApp? Mandánosla por otra vía:
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={handleEmailSubmit}
                        variant="outline"
                        className="flex-1 rounded-xl border-wood-200 text-wood-700 hover:bg-wood-50 py-5"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Enviar por correo
                      </Button>
                      <Button
                        onClick={handleCopy}
                        variant="outline"
                        className="flex-1 rounded-xl border-wood-200 text-wood-700 hover:bg-wood-50 py-5"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar mensaje
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar Info */}
            <div className="scroll-hidden" style={{ animationDelay: '150ms' }}>
              <div className="sticky top-24 space-y-6">
                <div className="p-8 rounded-2xl bg-wood-900 text-white">
                  <h3 className="font-heading font-bold text-white mb-5">¿Qué información incluir?</h3>
                  <ul className="space-y-3">
                    {[
                      'Tipo de producto o categoría',
                      'Medidas (si las conoces)',
                      'Cantidad aproximada',
                      'Uso o aplicación del material',
                      'Fecha estimada de entrega',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start space-x-3 text-sm text-white/70">
                        <div className="w-1.5 h-1.5 rounded-full bg-wood-300 mt-1.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 rounded-2xl border border-wood-200/60 bg-wood-50">
                  <h3 className="font-heading font-semibold text-wood-900 mb-4">Horario de atención</h3>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-4 h-4 text-wood-300 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-wood-600 space-y-1">
                      <p>{businessInfo.schedule.weekdays}</p>
                      <p>{businessInfo.schedule.saturday}</p>
                      <p className="text-wood-400">{businessInfo.schedule.sunday}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl border border-wood-200/60 bg-wood-50">
                  <h3 className="font-heading font-semibold text-wood-900 mb-4">También puedes</h3>
                  <div className="space-y-3">
                    <a
                      href={`tel:${businessInfo.phone}`}
                      onClick={trackPhone('cotizar_sidebar')}
                      className="flex items-center space-x-3 text-sm text-wood-700 hover:text-wood-900 transition-colors"
                    >
                      <Phone className="w-4 h-4 text-wood-300" />
                      <span>{businessInfo.phone}</span>
                    </a>
                    <a
                      href={`mailto:${businessInfo.email}`}
                      onClick={trackEmail('cotizar_sidebar')}
                      className="flex items-center space-x-3 text-sm text-wood-700 hover:text-wood-900 transition-colors"
                    >
                      <Mail className="w-4 h-4 text-wood-300" />
                      <span>{businessInfo.email}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manejo de objeciones: pedido minimo, tiempos, factura, formas de pago */}
      <section className="py-24 sm:py-32 bg-wood-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="scroll-hidden">
            <Faq
              title="Antes de cotizar"
              subtitle="Las dudas que nos llegan con más frecuencia."
            />
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Cotizar;
